import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import DeadlineCountdownBanner from '../DeadlineCountdownBanner';
import { ConsentModal } from '@/features/auth/components/ConsentModal';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { UserRole } from '@/types/global.types';
import { trackPageView } from '@/services/trackingService';

interface DashboardLayoutProps {
  title?: string;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ title }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Check if user needs to give consent (skip for SUPER_ADMIN and PARENT —
  // parents are covered by the enrollment contract, no in-app consent required)
  useEffect(() => {
    if (user && user.role !== UserRole.SUPER_ADMIN && user.role !== UserRole.PARENT) {
      // Show consent modal - it will auto-hide if user has all consents
      setShowConsentModal(true);
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* CNDP Consent Modal */}
      {showConsentModal && <ConsentModal />}
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar isCollapsed={false} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`
          transition-all duration-300
          ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
        `}
      >
        <Header
          onMenuClick={() => setMobileSidebarOpen(true)}
          title={title}
        />

        {/* Profile Deadline Countdown Banner */}
        <DeadlineCountdownBanner />

        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
